/*
 *
 * OrderManagement Sagas
 *
 * @version 1.0.0
 * @author Chiara Amato
 * @since 11-Jun-2024
 */

import {
  put,
  call,
  all,
  select,
  take
  // fork
} from 'redux-saga/effects'
import moment from 'moment'
import * as api from 'Api/orderManagement/orderManagement'
import * as actions from './OrderManagementActions'
import { doPopup } from 'Store/actions/popup'
import { getFormikForm, getUserTenantId, getUserMerchantId, getUserRoles, getOrderPagination } from 'Store/selectors'
import { deleteEmptyValues, userRolesString, exportDownload } from 'Utility/globalMethods'
import { doLocalLoading } from 'Store/actions/loading'
import { italianDateConverter, momentLongDateFormat } from 'Utility/dateMethods'
import { parseStringNumber } from 'Utility/numberMethods'
import formattedMessage from './languageModule'

const popupErrorResult = {
  title: formattedMessage.exportResultFailedTitle,
  isError: true
}

export function* orderSearchSaga(payload) {
  const orderSearchForm = yield select((state) => getFormikForm(state, 'OrderFilterForm'))
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    queryParams: {
      ...orderSearchForm,
      minAmount: orderSearchForm.minAmount ? parseStringNumber(orderSearchForm.minAmount) : null,
      maxAmount: orderSearchForm.maxAmount ? parseStringNumber(orderSearchForm.maxAmount) : null,
      fromTime: italianDateConverter(orderSearchForm.fromTime && moment(orderSearchForm.fromTime, momentLongDateFormat()), 'start'),
      toTime: italianDateConverter(orderSearchForm.toTime && moment(orderSearchForm.toTime, momentLongDateFormat()), 'end'),
      maxRecords: 11
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(rolesList)
    }
  })

  let type = ''
  if (payload?.searchType && payload?.page >= 0) {
    const { searchType, page } = payload
    type = searchType
    const pagination = yield select(getOrderPagination)

    if (searchType === 'more') {
      const fromTime = orderSearchForm.startDate ? moment(orderSearchForm.startDate, momentLongDateFormat()) : ''
      const toTime = moment(pagination[page].lastRecord)
      toTime.subtract(1, 'seconds')
      params.queryParams.toTime = italianDateConverter(toTime, 'time')
      if (fromTime) params.queryParams.fromTime = italianDateConverter(fromTime, 'time')
    }

    if (searchType === 'less') {
      const fromTime = moment(pagination[page].lastRecord)
      const toTime = moment(pagination[page].firstRecord)
      fromTime.subtract(1, 'seconds')
      toTime.add(1, 'seconds')
      params.queryParams.fromTime = italianDateConverter(fromTime, 'time')
      params.queryParams.toTime = italianDateConverter(toTime, 'time')
    }
  }
  
  yield put(doLocalLoading({ id: 'OrderManagementGetOrders', value: true }))
  yield put(actions.orderRecovery.request(params))
  const { error, response } = yield call(api.callGetOrders, params)
  if (!error) {
    yield put(actions.orderRecovery.success({ ...response.data, searchType: type }))
  } else {
    yield put(actions.orderRecovery.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'OrderManagementGetOrders', value: false }))
}

export function* getOrderDetailsSaga(payload) {
  const { orderId } = payload
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    merchantId,
    tenantId,
    roles: userRolesString(roleList)
  })

  yield put(doLocalLoading({ id: 'OrderManagementGetOrderDetails', value: true }))
  yield put(actions.doGetOrderDetails.request({ ...params }))
  const { error, response } = yield call(api.callGetOrderDetail, { urlParams: { orderId }, headers: params })
  if (!error) {
    yield put(actions.doGetOrderDetails.success(response, response.data))
  } else {
    yield put(actions.doGetOrderDetails.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'OrderManagementGetOrderDetails', value: false }))
}

export function* orderExportSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    queryParams: {
      fromDate: payload?.date?.from,
      toDate: moment(payload?.date.to).set('second', 59).toISOString()
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(rolesList)
    },
    format: payload.format
  })
  yield put(doLocalLoading({ id: 'exportOrders', value: true }))
  yield put(actions.retriveOrdersExportFile.request(params))
  const { error, response } = yield call(api.callRetrieveOrdersExportFile, params)
  if (!error) {
    yield put(actions.retriveOrdersExportFile.success(response.data))
    if (response?.data?.size === 0) {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionEmpty
        })
      ) // TODO: Add , outcome: 'failure' to the payload
    } else {
      const fileName = response?.headers?.['content-disposition']
        ? response.headers['content-disposition'].match(/filename="(.+)"/)[1]
        : `XPay_data.${
          payload?.format === 'csv' ? 'zip' : 'xlsx'
        }`
      const fileType = response?.headers?.['content-type']
        ? response.headers['content-type']
        : payload?.format === 'csv'
          ? 'application/zip'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      exportDownload(response.data, fileName, fileType)
      yield put(doPopup(''))
    }
  } else {
    if (error.status === 400 && error?.data?.errors[0]?.code === 'PS0135') {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionExcede
        })
      ) // TODO: Add , outcome: 'failure' to the payload
      yield put(actions.retriveOrdersExportFile.failure(response, { ...error, ignoreSystemError: true }))
    } else {
      yield put(doPopup(''))
      yield put(actions.retriveOrdersExportFile.failure(response, error))
    }
  }
  yield put(doLocalLoading({ id: 'exportOrders', value: false }))
}

// @generator saga

// @generator saga:watch

export function* watchGetOrderDetails() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_ORDER_DETAILS)
    yield call(getOrderDetailsSaga, { ...payload })
  }
}

export function* watchOrderSearch() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_ORDER_SEARCH)
    yield call(orderSearchSaga, { ...payload })
  }
}

export function* watchOrderExport() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_EXPORT_ORDERS)
    yield call(orderExportSaga, { ...payload })
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* rootSaga() {
  yield all([
    watchOrderSearch(),
    watchGetOrderDetails(),
    watchOrderExport()
    // @generator saga:fork
  ])
}
